import React, { useEffect, useMemo, useState, useCallback } from "react";

/**
 * R1d.11 — 12-hour inbound trend strip that sits directly beneath the
 * New This Hour tile. Reuses the same dark-lux tokens (no new visual
 * language). Truthful zero-bars for empty hours.
 *
 * Props:
 *   apiClient: an axios-like { get(path) } (typically the shared `api`)
 *   pollMs: refresh interval in ms (default 60_000)
 */
export default function HourlyTrendStrip({ apiClient, pollMs = 60000 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [hover, setHover] = useState(null); // { index } for tooltip

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get("/admin/owner/hourly-trend?window_hours=12");
      setData(r.data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    }
  }, [apiClient]);

  useEffect(() => {
    load();
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  const buckets = data?.buckets || [];
  const peak = Math.max(1, data?.peak_count ?? 0); // avoid div/0; peak=0 → 1
  const total = data?.total_last_window ?? 0;
  const avg = data?.average_per_hour ?? 0;
  const peakLabel = data?.peak_hour_label;
  const allZero = buckets.length > 0 && buckets.every((b) => b.count === 0);

  const ariaSummary = useMemo(() => {
    if (!buckets.length) return "Inbound trend loading";
    return `Inbound trend last 12 hours: ${total} total, peak of ${
      data?.peak_count ?? 0
    }${peakLabel ? " at " + peakLabel : ""}`;
  }, [buckets.length, total, data?.peak_count, peakLabel]);

  // Axis labels: first, middle, last bucket.
  const axisLabels = useMemo(() => {
    if (buckets.length < 3) return [];
    const first = buckets[0]?.hour_local_label;
    const mid = buckets[Math.floor(buckets.length / 2)]?.hour_local_label;
    const last = buckets[buckets.length - 1]?.hour_local_label;
    return [first, mid, last];
  }, [buckets]);

  if (err) {
    return (
      <div
        className="mt-2 text-[11px] font-mono text-red-700"
        data-testid="hourly-trend-error"
      >
        Hourly trend failed to load: {String(err)}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="mt-2 text-[11px] font-mono text-erf-dark/50"
        data-testid="hourly-trend-loading"
      >
        Loading 12-hour trend…
      </div>
    );
  }

  return (
    <div
      className="mt-3 rounded-md border border-erf-borderCream bg-white px-4 py-3"
      data-testid="hourly-trend-strip"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">
          12-hour inbound trend · {data.operating_timezone}
        </div>
        <div
          className="text-[11px] font-nav text-erf-dark/70"
          data-testid="hourly-trend-summary"
        >
          {allZero ? (
            <span className="text-erf-dark/40" data-testid="hourly-trend-empty">
              No inbound in the last 12 hours
            </span>
          ) : (
            <>
              Peak: <span className="font-semibold text-erf-dark">{data.peak_count}</span>
              {peakLabel ? (
                <>
                  {" "}
                  at <span className="font-mono text-erf-dark">{peakLabel}</span>
                </>
              ) : null}{" "}
              · Avg: <span className="font-mono text-erf-dark">{avg}</span>/hr
            </>
          )}
        </div>
      </div>

      <div
        role="img"
        aria-label={ariaSummary}
        className="mt-2 flex items-end gap-[3px] relative"
        style={{ height: 52 }}
        data-testid="hourly-trend-bars"
      >
        {buckets.map((b, i) => {
          const ratio = b.count / peak; // 0..1
          // Zero bars still show as a 2px baseline tick so the hour is visible.
          const heightPct = b.count === 0 ? 4 : Math.max(6, Math.round(ratio * 90));
          const isHovered = hover?.index === i;
          const isPeak = b.count === data.peak_count && b.count > 0;
          return (
            <div
              key={b.hour_utc}
              className="flex-1 flex items-end justify-center relative"
              style={{ height: "100%" }}
              onMouseEnter={() => setHover({ index: i })}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover({ index: i })}
              onBlur={() => setHover(null)}
              tabIndex={0}
              data-testid={`hourly-trend-bar-${i}`}
              aria-label={`${b.hour_local_label}: ${b.count} inbound`}
            >
              <div
                className={
                  "w-full rounded-sm transition-colors " +
                  (b.count === 0
                    ? "bg-erf-dark/10"
                    : isPeak
                    ? "bg-erf-gold"
                    : "bg-erf-gold/60")
                }
                style={{ height: `${heightPct}%` }}
              />
              {isHovered && (
                <div
                  className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-sm bg-erf-dark px-2 py-1 text-[10px] font-mono text-erf-cream shadow-lg pointer-events-none z-10"
                  data-testid={`hourly-trend-tooltip-${i}`}
                >
                  {b.hour_local_label} · {b.count}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {axisLabels.length === 3 && (
        <div className="mt-1 flex justify-between text-[10px] font-mono text-erf-dark/40">
          <span data-testid="hourly-trend-axis-start">{axisLabels[0]}</span>
          <span data-testid="hourly-trend-axis-mid">{axisLabels[1]}</span>
          <span data-testid="hourly-trend-axis-end">{axisLabels[2]}</span>
        </div>
      )}
    </div>
  );
}
